import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type CallbackStatus = 'verifying' | 'success' | 'error';

export function AuthCallback() {
  const [status, setStatus] = useState<CallbackStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      if (!supabase) {
        setStatus('error');
        setErrorMessage('Authentication is not configured.');
        return;
      }

      // Check for error in URL hash (OAuth/email failures)
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const errorCode = hashParams.get('error_code');
      if (errorCode) {
        setStatus('error');
        setErrorMessage(
          hashParams.get('error_description') || 'Authentication failed.'
        );
        return;
      }

      // Handle token_hash verification (email confirmation)
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type') as
        | 'email'
        | 'recovery'
        | 'invite'
        | 'magiclink'
        | undefined;

      if (tokenHash && type) {
        if (type === 'recovery') {
          // Redirect to password reset page with the token
          navigate(`/auth/reset-password?token_hash=${tokenHash}&type=${type}`, {
            replace: true,
          });
          return;
        }

        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type,
        });

        if (error) {
          setStatus('error');
          setErrorMessage(error.message);
          return;
        }

        setStatus('success');
        setTimeout(() => navigate('/', { replace: true }), 2000);
        return;
      }

      // If no token_hash, Supabase may have handled it via hash fragment (OAuth)
      // The onAuthStateChange listener in AuthContext will pick up the session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setStatus('success');
        setTimeout(() => navigate('/', { replace: true }), 1000);
      } else {
        setStatus('error');
        setErrorMessage('No valid authentication token found.');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-6">
      <div className="card p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="w-12 h-12 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Verifying your account...
            </h2>
            <p className="text-surface-400">Please wait a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Account verified!
            </h2>
            <p className="text-surface-400">
              Redirecting you to the app...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Verification failed
            </h2>
            <p className="text-surface-400 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="btn-primary"
            >
              Back to app
            </button>
          </>
        )}
      </div>
    </div>
  );
}
